import {
  Button,
  ButtonSize,
  ButtonVariant,
  Device,
  PopupTip,
  ResponsiveTypography,
  ResponsiveTypographyVariant,
  TEventFunction,
  useDeviceViewport,
  withTheme,
} from '@se-admin/falcon';
import { i18n } from '@secret-escapes/i18n';
import * as React from 'react';
import { TranslationsContext } from '../../common/contexts';
import { ApplicationContext, IApplication } from '../../common/contexts/ApplicationContext';
import {
  BookingDetailsDates,
  BookingDetailsHeader,
  BookingDetailsImage,
  BookingDetailsLogo,
  BookingDetailsRefundInformation,
  BookingHotelDetailsPopupContent,
  BookingOfferDetailsImageSize,
} from './BookingOfferDetails.components';
import { IBookingOfferDetailsProps } from './BookingOfferDetails.interfaces';
import {
  BookingDetailsGridWrapper,
  BookingDetailsHeaderContainer,
  BookingDetailsHeaderOverlay,
  BookingDetailsHeaderWrapper,
  BookingDetailsPageWrapper,
  BookingDetailsWrapper,
  BookingOfferDetailsContainer,
  TipContainer,
} from './BookingOfferDetails.styles';

/**
 * Shows hotel and offer detals at the top of the booking page.
 */
const BookingOfferDetails: React.FunctionComponent<IBookingOfferDetailsProps> = ({
  booking,
  isCancellable,
  cancellationInformation,
  originalOfferDetails,
  offerDetails,
  theme,
}) => {
  const deviceViewport: Device = useDeviceViewport();
  const isMobile: boolean = deviceViewport === Device.mobile;
  const applicationProps: IApplication = React.useContext(ApplicationContext);
  const { translations } = React.useContext(TranslationsContext);
  const onClick: () => void = () => {
    return;
  };

  return (
    <BookingOfferDetailsContainer data-testid="BookingOfferDetails">
      {booking && (
        <React.Fragment>
          {isMobile && (
            <BookingDetailsPageWrapper
              imageColumnWidth={BookingOfferDetailsImageSize.desktop.width}
            >
              <BookingDetailsHeaderContainer>
                <BookingDetailsGridWrapper>
                  <BookingDetailsImage leadImage={booking.leadImage} />
                </BookingDetailsGridWrapper>
                <BookingDetailsHeaderOverlay />
                <BookingDetailsHeaderWrapper>
                  <BookingDetailsHeader theme={theme} isMobile={isMobile} booking={booking} />
                </BookingDetailsHeaderWrapper>
              </BookingDetailsHeaderContainer>
              <BookingDetailsWrapper>
                <BookingDetailsRefundInformation
                  isCancellable={isCancellable}
                  cancellationInformation={cancellationInformation}
                />
                <TipContainer>
                  <ResponsiveTypography variant={ResponsiveTypographyVariant.body}>
                    {i18n('change.in.offer.details', [], translations)}
                  </ResponsiveTypography>
                </TipContainer>
                {offerDetails && originalOfferDetails && (
                  <PopupTip
                    noPadding={true}
                    content={
                      <BookingHotelDetailsPopupContent
                        offerDetails={offerDetails}
                        originalOfferDetails={originalOfferDetails}
                      />
                    }
                  >
                    {(popupTipOpen: TEventFunction) => {
                      return (
                        <div onClick={popupTipOpen}>
                          <Button
                            variant={ButtonVariant.secondary}
                            onClick={onClick}
                            fullWidth={true}
                          >
                            {i18n('sale.booking.viewOfferDetails', [], translations)}
                          </Button>
                        </div>
                      );
                    }}
                  </PopupTip>
                )}
                <BookingDetailsDates booking={booking} />
              </BookingDetailsWrapper>
            </BookingDetailsPageWrapper>
          )}
          {!isMobile && (
            <React.Fragment>
              <ResponsiveTypography variant={ResponsiveTypographyVariant.t4Serif}>
                {i18n('sale.booking.reviewAndConfirm.title', [], translations)}
              </ResponsiveTypography>
              <TipContainer>
                <ResponsiveTypography variant={ResponsiveTypographyVariant.body}>
                  {i18n('change.in.offer.details', [], translations)}
                </ResponsiveTypography>
              </TipContainer>
              <BookingDetailsPageWrapper
                imageColumnWidth={BookingOfferDetailsImageSize.desktop.width}
              >
                <BookingDetailsImage leadImage={booking.leadImage} />
                <BookingDetailsWrapper>
                  <BookingDetailsHeader theme={theme} isMobile={isMobile} booking={booking} />
                  <BookingDetailsDates booking={booking} />
                  {offerDetails && originalOfferDetails && (
                    <PopupTip
                      noPadding={true}
                      content={
                        <BookingHotelDetailsPopupContent
                          offerDetails={offerDetails}
                          originalOfferDetails={originalOfferDetails}
                        />
                      }
                    >
                      {(popupTipOpen: TEventFunction) => {
                        return (
                          <div onClick={popupTipOpen}>
                            <Button
                              variant={ButtonVariant.secondary}
                              size={ButtonSize.small}
                              onClick={onClick}
                            >
                              {i18n('sale.booking.viewOfferDetails', [], translations)}
                            </Button>
                          </div>
                        );
                      }}
                    </PopupTip>
                  )}
                </BookingDetailsWrapper>
                <BookingDetailsLogo
                  userTerritory={applicationProps.territoryCode}
                  isWhitelabel={applicationProps.affiliateConfig.isWhiteLabel}
                />
              </BookingDetailsPageWrapper>
              <BookingDetailsRefundInformation
                isCancellable={isCancellable}
                cancellationInformation={cancellationInformation}
              />
            </React.Fragment>
          )}
        </React.Fragment>
      )}
    </BookingOfferDetailsContainer>
  );
};

export default withTheme(BookingOfferDetails);
